import { FC, JSX, useMemo } from 'react';
import { Alert, Box, Chip, Sheet, Typography } from '@mui/joy';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import CategoryIcon from '@mui/icons-material/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { COMPANY_TYPE } from '@consolidocs-ui/utils';
import { CompanyType } from '@consolidocs-ui/types';
import { IndustryType } from '@consolidocs-ui/types';
import { ContractType } from '@consolidocs-ui/types';
import { useGetIndustriesQuery } from '@consolidocs-ui/data-access/IndustryApi';

interface Step2ReviewProps {
  formData: CompanyType;
}

/**
 * Step 2 — read-only review shown for **General** organizations, so
 * the wizard always has a meaningful second step and the primary Create
 * button lives on the final step.
 */
const Step2ReviewSummary: FC<Step2ReviewProps> = ({
  formData,
}): JSX.Element => {
  const { data: industries } = useGetIndustriesQuery();

  const industryName = useMemo(() => {
    const list: IndustryType[] = industries?.data || [];
    return list.find((i) => i.id === formData.industry)?.name || '—';
  }, [industries, formData.industry]);

  const contractTypeNames = useMemo<string[]>(() => {
    const list: IndustryType[] = industries?.data || [];
    const industry = list.find((i) => i.id === formData.industry);
    const contractMap = new Map<string, string>(
      (industry?.contract_types || []).map((ct: ContractType) => [ct.id, ct.name])
    );
    return (formData.contract_types || []).map(
      (id) => contractMap.get(id) || id
    );
  }, [industries, formData.industry, formData.contract_types]);

  const companyTypeLabel =
    COMPANY_TYPE.find((c) => c.value === formData.companyType)?.label || '—';

  const addressLine = [
    formData.address1,
    formData.address2,
    formData.city,
    formData.state,
    formData.postalCode,
    formData.country,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Box className="wizard-step review-step">
      <Alert
        variant="soft"
        color="primary"
        className="review-banner"
        startDecorator={<InfoOutlinedIcon className="review-banner__icon" />}
      >
        <Box className="review-banner__text">
          <Typography level="title-sm" className="review-banner__title">
            Review &amp; Confirm
          </Typography>
          <Typography level="body-sm" className="review-banner__body">
            Check the details below, then click{' '}
            <strong>Create Organization</strong> to finish. General organizations
            don&apos;t require infrastructure configuration.
          </Typography>
        </Box>
      </Alert>

      <Sheet variant="outlined" className="review-card">
        <Box className="review-section">
          <Box className="review-section-header">
            <BusinessIcon fontSize="small" />
            <Typography level="title-sm">Organization</Typography>
          </Box>
          <Box className="review-grid">
            <ReviewItem label="Name" value={formData.name} />
            <ReviewItem label="Admin Email" value={formData.email} />
            <ReviewItem label="Phone" value={formData.phone} />
            <ReviewItem label="Website" value={formData.website} />
            <ReviewItem label="Company Type" value={companyTypeLabel} />
            <ReviewItem
              label="Description"
              value={formData.description}
              span="full"
            />
          </Box>
        </Box>

        <Box className="review-section">
          <Box className="review-section-header">
            <PlaceIcon fontSize="small" />
            <Typography level="title-sm">Address</Typography>
          </Box>
          <Box className="review-grid">
            <ReviewItem label="Address" value={addressLine} span="full" />
          </Box>
        </Box>

        <Box className="review-section">
          <Box className="review-section-header">
            <CategoryIcon fontSize="small" />
            <Typography level="title-sm">Categorization</Typography>
          </Box>
          <Box className="review-grid">
            <ReviewItem label="Industry" value={industryName} />
            <ReviewItem
              label="Contract Types"
              span="full"
              valueNode={
                contractTypeNames.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {contractTypeNames.map((n) => (
                      <Chip key={n} size="sm" variant="soft" color="primary">
                        {n}
                      </Chip>
                    ))}
                  </Box>
                ) : (
                  <Typography level="body-sm" color="neutral">
                    —
                  </Typography>
                )
              }
            />
          </Box>
        </Box>
      </Sheet>
    </Box>
  );
};

interface ReviewItemProps {
  label: string;
  value?: string;
  valueNode?: JSX.Element;
  /** "full" makes the item span the entire grid row. */
  span?: 'full';
}

const ReviewItem: FC<ReviewItemProps> = ({
  label,
  value,
  valueNode,
  span,
}): JSX.Element => (
  <Box
    className={`review-item${span === 'full' ? ' review-item--full' : ''}`}
  >
    <Typography level="body-xs" className="review-label">
      {label}
    </Typography>
    {valueNode ? (
      valueNode
    ) : (
      <Typography level="body-sm" className="review-value">
        {value?.trim() ? value : '—'}
      </Typography>
    )}
  </Box>
);

export default Step2ReviewSummary;
